import {AllSpiceJsonLoader} from './allspicejson.ts';
import {htmlEscape} from 'escape-goat';
import {escapePathSegments} from '../utils.ts';
import {createTippy} from '../modules/tippy.ts';
import type {Container} from '../container.ts';
import type {SchematicExport} from '../../generated/crysknife/types.ts';

/**
 * Add a tooltip to the reference
 * @param {Element} ref
 * @param {string} link
 * @param {string} designator
 * @param {string} componentId
 */
function createTooltip(ref: Element, link: string, designator: string, componentId: string) {
  const el = document.createElement('div');
  el.classList.add('tw:p-3');

  const title = document.createElement('h3');
  title.textContent = designator;

  const divider = document.createElement('div');
  divider.classList.add('ui', 'divider');

  const contents = document.createElement('a');
  contents.setAttribute('href', `${link}?comp=${encodeURIComponent(componentId)}`);
  const docName = link.split('/').pop();
  contents.textContent = docName ?? '';

  el.append(title);
  el.append(divider);
  el.append(contents);

  if (ref.parentNode) {
    ref.parentNode.insertBefore(el, ref.nextSibling);

    createTippy(ref, {
      theme: 'default',
      content: el,
      placement: 'top-start',
      interactive: true,
      role: 'tooltip',
      interactiveBorder: 5,
    });
  }
}

export const AllSpiceComponentRefs = {
  buildSvgUrls(
    container: Container,
    repoOwner: string,
    repoName: string,
    commit: string,
    documents: Record<string, string>,
  ): Record<string, [string, string]> {
    const urls: Record<string, [string, string]> = {};
    for (const [docId, docPath] of Object.entries(documents)) {
      const jsonUrl = AllSpiceJsonLoader.buildUrl(container, repoOwner, repoName, commit, docPath);
      const docUrl = this.buildSvgLinkUrl(container, repoOwner, repoName, commit, docPath);
      if (jsonUrl && docUrl) {
        urls[docId] = [jsonUrl, docUrl];
      }
    }
    return urls;
  },

  /**
   * Builds the URL to link to for component reference hyperlinks
   * @param {Container} container
   * @param {string} repoOwner
   * @param {string} repoName
   * @param {string} commit
   * @param {string} treePath
   * @returns {string | null}
   */
  buildSvgLinkUrl(
    container: Container,
    repoOwner: string,
    repoName: string,
    commit: string,
    treePath: string): string | null {
    /** @type {string} */
    const appSubUrl = container.lookup('appSubUrl');
    return treePath ? `${appSubUrl}/${repoOwner}/${repoName}/src/commit/${commit}/${escapePathSegments(treePath)}` : null;
  },

  /**
   * Resolve component refs for a given svg, include link to referenced document as a data attribute
   * @param {Container} container
   * @param {SVGElement} svg
   * @param {Object.<string, Array<string>>} urls
   * @return {Promise<void[]>}
   */
  forSvg(container: Container, svg: SVGElement, urls: Record<string, [string, string]>) {
    const promises = [];

    for (const ref of svg.querySelectorAll('[data-component-ref]')) {
      const ids = ref.getAttribute('data-component-ref').split('\\');
      if (ids.length !== 2) {
        console.error(`data-component-ref incorrectly formatted: ${ref.getAttribute('data-component-ref')}`);
        continue;
      }

      const [docId, componentId] = ids;
      const updateComponent = async () => {
        try {
          const [jsonUrl, docUrl] = urls[docId];
          const results = await AllSpiceJsonLoader.load(container, jsonUrl, null);
          const headJson = results.headJson as SchematicExport & Record<string, unknown>;
          let componentFound = false;
          let text = '';

          // We are dealing with crysknife output
          if (headJson.pages) {
            const component = headJson.pages[0]?.components?.[componentId] ?? null;
            componentFound = component !== null;
            text = component?.reference ?? '';
          } else if (
            // We are dealing with tools output
            // TODO(ID-3147): remove this branch when tools is removed
            componentId in headJson &&
            typeof headJson[componentId] === 'object' &&
            headJson[componentId] !== null
          ) {
            const toolsComponent = headJson[componentId] as {
              attributes?: {Designator?: {text?: string}};
              type?: string;
            };
            if (toolsComponent.type === 'Component') {
              componentFound = true;
              text = toolsComponent.attributes?.Designator?.text ?? '';
            }
          }

          if (componentFound && text) {
            ref.innerHTML = htmlEscape(text);
            ref.classList.add('allspice-elaborate');

            // border-bottom won't work for svg, use text-decoration instead
            ref.setAttribute('style', 'text-decoration: underline; text-decoration-style: dotted');
            createTooltip(ref, docUrl, text, componentId);
          } else {
            // not component or not found
            const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            title.innerHTML = 'Failed to resolve reference';
            ref.append(title);
          }
        } catch (e) {
          console.error(e);
        }
      };

      if (docId in urls) {
        promises.push(updateComponent());
      }
    }
    return Promise.all(promises);
  },
};
